import { apiClient } from '@/api/client'

/**
 * `downloadAttachment` — save an attachment to disk under its original
 * filename.
 *
 * The work happens server-side: `?disposition=attachment` makes the BE sign
 * the S3 GET with `Content-Disposition: attachment`, so S3 itself tells the
 * browser to save rather than render, and supplies the filename (the S3 key
 * is a UUID). Navigating to that URL then downloads without leaving the page.
 *
 * The obvious client-side alternative — `fetch` the bytes, wrap them in a
 * blob, point an `<a download>` at it — was tried and is worse on three
 * counts: a cross-origin `<a download>` filename is ignored without the
 * server header anyway, `fetch` requires the caller's origin to be on the
 * bucket's CORS allowlist (`https://*.3mote.com` only, so it fails on
 * localhost), and it copies the whole file through the JS heap — up to the
 * 200 MiB per-file ceiling.
 *
 * Throws whatever `apiClient` throws (404 on a deleted attachment, 401 on an
 * expired session) so callers can surface a toast; a silent no-op reads as a
 * dead button.
 */
export async function downloadAttachment(attachmentId: string): Promise<void> {
  const res = await apiClient<{ data: { url: string } }>(
    `/api/uploads/${attachmentId}/url?as=json&disposition=attachment`,
  )
  triggerBrowserDownload(res.data.url)
}

/**
 * Point the browser at a URL that answers with `Content-Disposition:
 * attachment`. A hidden anchor rather than `location.href` — the anchor
 * can't navigate the SPA away if the response ever arrives without the
 * header, and `rel="noopener"` keeps the opener reference out of reach.
 */
function triggerBrowserDownload(url: string): void {
  const link = document.createElement('a')
  link.href = url
  link.rel = 'noopener'
  // Ignored cross-origin, but harmless, and it's the correct hint when the
  // URL is ever same-origin.
  link.download = ''
  document.body.appendChild(link)
  link.click()
  link.remove()
}
